"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";



function SinglePostSkeleton() {
  return (
    <div className="animate-pulse space-y-8">
      <div className="h-12 w-3/4 bg-muted rounded-md" />
      <div className="flex gap-4">
        <div className="h-4 w-24 bg-muted rounded-md" />
        <div className="h-4 w-24 bg-muted rounded-md" />
      </div>
      <div className="w-full aspect-video bg-muted rounded-xl" />
      <div className="space-y-4">
        <div className="h-4 w-full bg-muted rounded-md" />
        <div className="h-4 w-full bg-muted rounded-md" />
        <div className="h-4 w-5/6 bg-muted rounded-md" />
      </div>
    </div>
  );
}

function RelatedPostsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-80 bg-muted rounded-xl animate-pulse" />
      ))}
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return <SinglePostSkeleton />;
  
  if (error) {
    return (
      <div className="p-8 text-center text-destructive bg-destructive/10 rounded-xl">
        <p>Si è verificato un errore nel caricamento dell'articolo.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground bg-muted/20 rounded-xl">
        <p>Articolo non trovato.</p>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("it-IT", { day: 'numeric', month: 'long', year: 'numeric' }) : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="mb-8 md:mb-12 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground mb-6">
          {post.categories && post.categories.length > 0 && (
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
              {post.categories[0].name}
            </span>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{date}</span>
          </div>
          {post.author && (
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        <h1 className="article-title text-3xl md:text-5xl font-bold tracking-tight text-foreground leading-tight mb-6">
          {post.title}
        </h1>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-10 md:mb-14 rounded-2xl overflow-hidden shadow-lg border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div 
        className="prose prose-lg dark:prose-invert max-w-none mb-12 [&_p]:mb-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10">
          {post.tags.map((tag) => (
            <div key={tag.id} className="flex items-center gap-1 text-sm text-muted-foreground bg-muted px-3 py-1 rounded-md">
              <Tag className="w-3 h-3" />
              {tag.name}
            </div>
          ))}
        </div>
      )}

      <Separator className="my-10" />

      {}
      {post.author && (
        <div className="bg-muted/30 border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
          <Avatar className="w-20 h-20 border-2 border-background shadow-sm">
            <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
            <AvatarFallback className="text-lg">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-2">Scritto da {post.author.name}</h3>
            <p className="text-muted-foreground mb-4">
              Esperto legale presso DCLegal. Si occupa di diritto civile e contrattualistica d'impresa, offrendo consulenza strategica per aziende e privati.
            </p>
            <Link to={post.author.link || "#"}>
              <Button variant="outline" size="sm">
                Vedi tutti gli articoli
              </Button>
            </Link>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return <RelatedPostsSkeleton />;
  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden hover:shadow-md transition-shadow duration-300 border-border/60">
          <div className="relative aspect-video overflow-hidden bg-muted">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">Nessuna immagine</span>
              </div>
            )}
          </div>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Calendar className="w-3 h-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString("it-IT") : ""}</span>
            </div>
            <CardTitle className="article-preview-title text-xl line-clamp-2 leading-tight">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <div 
              className="text-muted-foreground text-sm line-clamp-3"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group px-0 hover:bg-transparent hover:text-primary">
                Leggi l'articolo
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const [mainQuery, setMainQuery] = useState<WP_Query | null>(null);
  const [relatedQuery, setRelatedQuery] = useState<WP_Query | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).wvcClient) {
      const currentParams = (window as any).wvcClient.getCurrentWPQueryParams();
      setMainQuery(new WP_Query(currentParams));
      
      
      
      setRelatedQuery(new WP_Query({
        post_type: "post",
        posts_per_page: 3,
        ignore_sticky_posts: true,
      }));
    }
  }, []);

  if (!mainQuery || !relatedQuery) {
    return <div className="min-h-screen bg-background" />; 
  }

  return (
    <section id="single-post-template-section" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <PostsProvider wp_query={mainQuery}>
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-20 md:mt-28">
          <div className="flex items-center justify-between mb-8 md:mb-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Potrebbe interessarti anche</h2>
              <p className="text-muted-foreground mt-2">Ultime notizie e approfondimenti dal nostro blog.</p>
            </div>
            <Link to="/blog" className="hidden md:block">
              <Button variant="outline">Vedi tutti</Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>

          <div className="mt-8 md:hidden text-center">
            <Link to="/blog">
              <Button variant="outline" className="w-full">Vedi tutti gli articoli</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}